import type FVector from '@data/geometry/FVector';
import '@data/Simulations/pipeline/blocks/InsidePolygonBlock';
import type { InsidePolygonParameters } from '@data/Simulations/pipeline/blocks/InsidePolygonBlock';
import { PolygonDrawer } from '@gui/components/DrawModule/PolygonDrawer';
import { Panels } from '@gui/constants/Panels';
import { ControllerManager } from '@gui/controller/ControllerManager';
import usePersistentStore from '@gui/store/persistent/persistentStore';
import { Box, Button, Checkbox, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { DrawModule } from '@rendering/DrawingModule/DrawingModule';
import { PolygonDrawerManager } from '@rendering/DrawingModule/PolygonDrawerManager';
import type { BlockParameters } from '@visualizer/types';
import Grid from 'Geodata/grid';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type InsidePolygonParametersProps = {
    blockId: string;
};

const buildBuildablePolygon = () => Grid.getBuildableBorderPoints();

export const getInsidePolygonDefaultParams = (): Pick<InsidePolygonParameters, 'polygon' | 'invert'> => ({
    polygon: buildBuildablePolygon(),
    invert: false,
});

const InsidePolygonParameters: FC<InsidePolygonParametersProps> = ({ blockId }) => {
    // Controllers:
    const pipelineController = useMemo(() => ControllerManager.getInstance().getPipelineController(), []);
    const updateParams = (partial: Partial<InsidePolygonParameters>) => {
        pipelineController.updateBlockParams(blockId, partial as BlockParameters);
    };
    const [polygon, setPolygon] = useState<FVector[]>(() => {
        const params = pipelineController.getBlockParams(blockId) as InsidePolygonParameters | undefined;
        return params?.polygon ?? [];
    });
    const [invert, setInvert] = useState<boolean>(() => {
        const params = pipelineController.getBlockParams(blockId) as InsidePolygonParameters | undefined;
        return params?.invert ?? false;
    });

    // Zustand:
    const storedMode = usePersistentStore((state) => {
        const id = state.layers.activeLayerID;
        return id ? state.layers.layerStates[id]?.pipelineGUIParams?.[blockId]?.insidePolygonMode : undefined;
    });
    const enabled = usePersistentStore((state) => {
        const id = state.layers.activeLayerID;
        return state.layers.layerStates[id]?.pipeline.blocks.find((block) => block.id === blockId)?.params.enabled;
    });

    // React State:
    const [editing, setEditing] = useState(false);

    const restoreGridRef = useRef<boolean>(false);

    const constrainPolygon = useMemo(() => Grid.getPoints(), []);
    const buildablePolygon = useMemo(buildBuildablePolygon, []);

    // Other variables:
    const insidePolygonMode: 'buildable' | 'custom' = storedMode ?? (buildablePolygon.length ? 'buildable' : 'custom');

    // Draw Module:
    const onSave = useCallback(
        (pts: FVector[], failed: boolean) => {
            if (!failed) {
                // 1) Update runtime state + Polygon params:
                setPolygon(pts);
                updateParams({ polygon: pts });
            }

            // 2) RestoreGrid if necessary (Done both for Failed and non statuses):
            restoreGridIfNeeded();

            // 3) Dispose DrawModule:
            if (failed) {
                drawModule.visualEffects.removeSpheres();
                drawModule.visualEffects.removePolyLine();
                const points = [...polygon, polygon[0]];
                drawModule.visualEffects.generatePolyLine(points);
            }
        },
        [updateParams]
    );

    const manager = PolygonDrawerManager.getInstance();

    const drawModule = manager.getOrCreate(blockId, () => new DrawModule(onSave, constrainPolygon));

    // =====================================
    // EFFECTS
    // =====================================

    // Auto-Start Drawing when in Polygonal Mode:
    const autoStartedRef = useRef(false);
    useEffect(() => {
        if (insidePolygonMode !== 'custom') {
            autoStartedRef.current = false;
            return;
        }

        if (autoStartedRef.current) return;
        autoStartedRef.current = true;

        beginEdit();
    }, [insidePolygonMode]);

    // Syncs Editing button when edit is closed via "X" icon:
    useEffect(() => {
        const onStarted = () => setEditing(true);
        const onStopped = () => setEditing(false);

        drawModule.on?.('started', onStarted);
        drawModule.on?.('stopped', onStopped);

        return () => {
            drawModule.off?.('started', onStarted);
            drawModule.off?.('stopped', onStopped);
        };
    }, [drawModule]);

    useEffect(() => {
        return () => {
            // 1) Stop DrawModule:
            if (drawModule.isRunning()) {
                drawModule.stop(true);
            }

            // 2) RestoreGrid if necessary:
            restoreGridIfNeeded();
        };
    }, []);

    // Update OnSave (needed on remounts to keep OnSave, the polygon, up to date):
    useEffect(() => {
        drawModule.setOnSaveCallback(onSave);
    }, [drawModule, onSave]);

    // Disables/Renables drawer when the block is deactivated/reactivated:
    useEffect(() => {
        if (!enabled) {
            // 1) Stop editing
            endEdit();

            // 2) Remove visuals
            drawModule.visualEffects.removeSpheres();
            drawModule.visualEffects.removePolyLine();

            return;
        }

        // Re-enabled
        if (insidePolygonMode === 'custom' && polygon.length) {
            // 1) Restore existing polygon visuals:
            const points = [...polygon, polygon[0]];
            drawModule.visualEffects.generatePolyLine(points);
        }
    }, [enabled]);

    // =====================================
    // HELPERS
    // =====================================

    const rememberGridState = () => {
        const store = usePersistentStore.getState();
        restoreGridRef.current = store.global.context.showGrid;
    };

    const restoreGridIfNeeded = () => {
        if (!restoreGridRef.current) return;

        const store = usePersistentStore.getState();
        store.setPanel({ showGrid: true }, Panels.CONTEXT);
        restoreGridRef.current = false;
    };

    // =====================================
    // HANDLERS
    // =====================================

    // Start drawing & deactivate sensors:
    const beginEdit = () => {
        if (drawModule.isRunning()) return;

        // 1) Remember current Grid state & Deactivate (if not already):
        rememberGridState();

        if (restoreGridRef.current) {
            usePersistentStore.getState().setPanel({ showGrid: false }, Panels.CONTEXT);
        }

        // 2) Start drawing:
        drawModule.tryActivate();
    };

    // Stop drawing & re-activate sensors:
    const endEdit = () => {
        if (!drawModule.isRunning()) return;

        // 1) Re-activate Grid:
        restoreGridIfNeeded();

        // 2) Stop drawing:
        drawModule.stop();
    };

    // Switch between Custom drawing & Buildable:
    const setMode = (mode: 'buildable' | 'custom') => {
        const store = usePersistentStore.getState();
        const layer = store.getActiveLayerState();
        if (!layer) return;

        store.updateCurrentLayerState({
            pipelineGUIParams: {
                ...(layer.pipelineGUIParams ?? {}),
                [blockId]: {
                    ...(layer.pipelineGUIParams?.[blockId] ?? {}),
                    insidePolygonMode: mode,
                },
            },
        });
    };

    const handleModeChange = (_event: React.ChangeEvent<HTMLInputElement>, value: string) => {
        if (value === 'buildable') {
            endEdit();
            setMode('buildable');
            updateParams({ polygon: buildablePolygon });
        } else {
            setMode('custom');
        }
    };

    const handleInvertChange = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setInvert(checked);
        updateParams({ invert: checked });
    };

    // Actual HTML:
    return (
        <Box className="panel-compact-control-stack">
            <RadioGroup row value={insidePolygonMode} onChange={handleModeChange} sx={{ gap: 0, mb: 1.5 }}>
                <FormControlLabel value="buildable" control={<Radio size="small" />} label="Buildable polygon" />
                <FormControlLabel
                    value="custom"
                    control={<Radio size="small" />}
                    label="Draw polygon"
                    data-testid="polygon-mode-custom-label"
                />
            </RadioGroup>

            {insidePolygonMode === 'custom' && (
                <>
                    <Button size="small" variant="contained" onClick={() => (editing ? endEdit() : beginEdit())}>
                        {editing ? 'Stop Editor' : polygon.length ? 'Edit' : 'Draw'}
                    </Button>

                    <PolygonDrawer isActive={editing} initialPoints={polygon} drawModule={drawModule} />
                </>
            )}

            <FormControlLabel
                control={<Checkbox size="small" checked={invert} onChange={handleInvertChange} />}
                label="Invert"
                sx={{ mt: 1 }}
            />
        </Box>
    );
};

export default InsidePolygonParameters;
